import {
  Button,
  ButtonWithConfirmation,
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@readcloud/component-library';
import {
  Annotation,
  KnownCloud,
  KnownUser,
  UserPrefState,
} from '@readcloud/data';
import tailwindConfig from '@root/tailwind.config';
import React, { RefObject } from 'react';
import { MdDelete, MdEdit, MdShare } from 'react-icons/md';
import { AnnotationEditor } from './AnnotationEditor';
import { SharingPopover } from './SharingPopover';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  outerContainer: HTMLDivElement;
  pagesContainer: HTMLDivElement;
  pageContainer: HTMLDivElement;
  annotation: Annotation;
  reference?: HTMLElement;
  onDelete: (id: string) => void;
  onComment: (id: string) => void;
  onChange: (annotation: Annotation) => void;
  knownUsers: KnownUser[];
  knownClouds: KnownCloud[];
  showPublisherOptions: boolean;
  zIndex: number;
  onSavePreferences?: (type: string, data: Partial<Annotation['data']>) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
  onEditOpen?: (open: boolean | ((value: boolean) => boolean)) => void;
  onShareOpen?: (open: boolean | ((value: boolean) => boolean)) => void;
  editOpen?: boolean;
  shareOpen?: boolean;
}

export const AnnotationToolbarModals = (props: Props) => {
  const handleDelete = React.useCallback(() => {
    if (props.annotation) props.onDelete(props.annotation.id);
  }, [props.annotation && props.annotation.id, props.onDelete]);

  const mobileSizeinPx = tailwindConfig?.theme?.screens?.['sm'] || '640px';
  const mobileSize = parseInt(mobileSizeinPx, 10);

  const mobileView = React.useMemo(() => {
    if (mobileSize) {
      return window.innerWidth <= mobileSize;
    }
    return false;
  }, [mobileSizeinPx, window.innerWidth]);

  return (
    mobileView && (
      <div className='rc-w-full'>
        <div className="rc-overflow-hidden rc-rounded-md rc-z-[1500] rc-isolate rc-flex rc-w-full rc-bg-primary rc-text-primary-foreground">
          <Button
            variant="light"
            className="rc-h-10 rc-text-background hover:rc-text-primary"
            onClick={() => props.onEditOpen((s) => !s)}
          >
            <div className="rc-flex rc-justify-start rc-w-full rc-items-center rc-gap-2">
              <MdEdit size="16"/>
              <span className='rc-text-[12px]'>Edit</span>
            </div>
          </Button>

          <ButtonWithConfirmation
            title="Delete Annotation"
            confirmText="Delete"
            confirmVariant="danger"
            message="Are you sure you want to delete this annotation?"
            onConfirm={handleDelete}
            renderButton={
              <Button variant="light" className="rc-h-10 rc-text-background hover:rc-text-primary">
                <div className="rc-flex rc-justify-start rc-w-full rc-items-center rc-gap-2 ">
                  <MdDelete size="16" />
                  <span className='rc-text-[12px]'>Delete</span>
                </div>
              </Button>
            }
          />
          {props.knownUsers.length || props.knownClouds.length ? (
            <Button
              variant="light"
              className="rc-h-10 rc-text-background hover:rc-text-primary"
              onClick={() => props.onShareOpen((s) => !s)}
            >
              <div className="rc-flex rc-justify-start rc-w-full rc-items-center rc-gap-2">
                <MdShare size="16" />
                <span className='rc-text-[12px]'>Share</span>
              </div>
            </Button>
          ) : null}
        </div>

        <Modal
          isOpen={props.shareOpen}
          toggle={() => props.onShareOpen((s) => !s)}
          wrapClassName="rc-h-screen rc-overflow-auto rc-w-full"
        >
          <ModalHeader>
            <ModalTitle>Share Annotation</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <SharingPopover
              knownUsers={props.knownUsers}
              knownClouds={props.knownClouds}
              annotation={props.annotation}
              onChange={props.onChange}
            />
          </ModalBody>
        </Modal>
        <Modal
          isOpen={props.editOpen}
          toggle={() => props.onEditOpen((s) => !s)}
          wrapClassName="rc-h-screen rc-overflow-auto rc-w-full"
        >
          <ModalHeader>
            <ModalTitle>Edit Annotation</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <AnnotationEditor
              showPublisherOptions={props.showPublisherOptions}
              annotation={props.annotation}
              submitLabel="Save"
              onChange={props.onChange}
              autoSubmit
              onSavePreferences={props.onSavePreferences}
              annoPrefs={props.annoPrefs}
            />
          </ModalBody>
        </Modal>
      </div>
    )
  );
};

export default AnnotationToolbarModals;
